import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { blogService } from "@/services/blog";
import BlogContent from "@/components/blog/BlogContent";
import BlogCard from "@/components/blog/BlogCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Heart, Share2, Bookmark, FileText, Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from "@/contexts/AuthContext";

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [readingProgress, setReadingProgress] = React.useState(0);
  const { user } = useAuth();
  const [isSaved, setIsSaved] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState(false);

  // Fetch post data
  const { data: post, isLoading: isLoadingPost } = useQuery({
    queryKey: ["blogPost", slug],
    queryFn: () => blogService.getPost(slug!),
    enabled: !!slug,
  });

  // Fetch related posts
  const { data: relatedPosts, isLoading: isLoadingRelated } = useQuery({
    queryKey: ["relatedPosts", slug],
    queryFn: () => blogService.getRelatedPosts(slug!, 3),
    enabled: !!slug && !!post,
  });

  // Track reading progress
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      const element = contentRef.current;
      const totalHeight = element.scrollHeight - element.clientHeight;
      const progress = (element.scrollTop / totalHeight) * 100;
      setReadingProgress(Math.min(100, Math.max(0, progress)));
    };
    const element = contentRef.current;
    if (element) {
      element.addEventListener("scroll", handleScroll);
      return () => element.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Increment view count when post is loaded
  useEffect(() => {
    if (post) {
      blogService.incrementViews(post.id);
    }
  }, [post]);

  React.useEffect(() => {
    if (user && post) {
      const saved = JSON.parse(localStorage.getItem(`saved_posts_${user.id}`) || "[]");
      setIsSaved(saved.includes(String(post.id)));
    }
  }, [user, post]);

  const handleSave = () => {
    if (!user || !post) return;
    const key = `saved_posts_${user.id}`;
    let saved = JSON.parse(localStorage.getItem(key) || "[]");
    if (saved.includes(String(post.id))) {
      saved = saved.filter((id: string) => id !== String(post.id));
      setIsSaved(false);
    } else {
      saved.push(String(post.id));
      setIsSaved(true);
    }
    localStorage.setItem(key, JSON.stringify(saved));
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch (e) {
      setIsCopied(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (e) {}
    } else {
      handleCopyLink();
    }
  };

  if (isLoadingPost) {
    return (
      <div className="min-h-screen bg-sauna-beige py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-3/4 mb-4" />
          <Skeleton className="h-4 w-1/2 mb-8" />
          <Skeleton className="aspect-video w-full mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-sauna-beige py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-sauna-navy mb-4">Post not found</h1>
          <p className="text-muted-foreground mb-8">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild>
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | SaunaDip Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        {post.image_url && (
          <meta property="og:image" content={post.image_url} />
        )}
      </Helmet>

      <div className="min-h-screen bg-sauna-beige">
        {/* Reading Progress Bar */}
        <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
          <div
            className="h-full bg-sauna-orange transition-all duration-150"
            style={{ width: `${readingProgress}%` }}
          />
        </div>

        <div className="max-w-4xl mx-auto py-12 px-4">
          <Button
            variant="ghost"
            className="mb-8 -ml-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <Card className="border-none shadow-lg">
            <CardContent className="p-8">
              {/* Header */}
              <header className="mb-8 text-center">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-4">
                  <FileText className="h-5 w-5 text-sauna-orange" />
                  <time dateTime={post.publishedAt} className="text-sauna-navy/80">
                    {formatDate(post.publishedAt)}
                  </time>
                  {post.category && (
                    <>
                      <span>•</span>
                      <Link
                        to={`/blog/category/${post.category.slug}`}
                        className="hover:text-sauna-orange text-sauna-navy/80"
                      >
                        {post.category.name}
                      </Link>
                    </>
                  )}
                </div>
                <h1 className="text-4xl font-bold text-sauna-navy flex items-center justify-center gap-2">
                  {post.title}
                </h1>
                <div className="h-1 w-32 divider-gradient rounded-full mx-auto mt-4 mb-6"></div>
                <p className="text-xl text-sauna-navy/80 mb-6">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-center gap-4 mb-4">
                  {post.author && (
                    <div className="flex items-center gap-3">
                      {post.author.avatar && (
                        <img
                          src={post.author.avatar}
                          alt={post.author.name}
                          className="w-10 h-10 rounded-full"
                        />
                      )}
                      <div>
                        <div className="font-medium text-sauna-navy">{post.author.name}</div>
                        {post.author.bio && (
                          <div className="text-sm text-sauna-navy/80">
                            {post.author.bio}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSave}
                      className={isSaved ? "text-sauna-orange" : "hover:text-sauna-orange"}
                      aria-label={isSaved ? "Unsave post" : "Save post"}
                    >
                      <Heart className={`h-5 w-5 ${isSaved ? "fill-sauna-orange" : ""}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleCopyLink}
                      aria-label="Copy link"
                    >
                      <Copy className="h-5 w-5" />
                      {isCopied && <span className="ml-1 text-xs text-sauna-orange">Copied!</span>}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleShare}
                      aria-label="Share post"
                    >
                      <Share2 className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </header>

              {/* Featured Image */}
              {post.image_url && (
                <div className="mb-8 rounded-lg overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full aspect-video object-cover"
                    onError={(e) => {
                      console.warn(`Failed to load featured image for blog post: ${post.title}`);
                      e.currentTarget.src = "/assets/graphics/hero1.png";
                    }}
                  />
                </div>
              )}

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8 justify-center">
                  {post.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="hover:bg-sauna-orange/10"
                    >
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Content */}
              <div
                ref={contentRef}
                className="prose prose-lg max-w-none mb-12 mx-auto text-sauna-navy/90"
                style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
              >
                <BlogContent post={post} />
              </div>

              {/* Related Posts */}
              {!isLoadingRelated && relatedPosts && relatedPosts.length > 0 && (
                <section className="mt-16 pt-8 border-t">
                  <h2 className="text-2xl font-bold text-sauna-navy mb-6 text-center flex items-center justify-center gap-2">
                    <FileText className="h-6 w-6 text-sauna-orange" />
                    Related Articles
                  </h2>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {relatedPosts.map((relatedPost) => (
                      <BlogCard key={relatedPost.id} post={relatedPost} />
                    ))}
                  </div>
                </section>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
